// FILE: src/components/Admin/AI/kb/ManagedUploadStatusPanel.tsx
import React from 'react';
import {
  Alert,
  Box,
  Chip,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

type Props = {
  status?: any;
  busy?: boolean;
};

function statusColor(value?: string): 'success' | 'warning' | 'error' | 'info' | 'default' {
  const s = String(value || '').toLowerCase();

  if (['success', 'succeeded', 'completed', 'complete', 'active'].includes(s)) return 'success';
  if (['pending', 'running', 'processing', 'queued', 'ingesting'].includes(s)) return 'warning';
  if (['failed', 'failure', 'error', 'cancelled', 'canceled', 'disabled'].includes(s)) return 'error';
  if (['unknown'].includes(s)) return 'info';

  return 'default';
}

function valueOf(...values: any[]) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '') return value;
  }

  return null;
}

function stringifyValue(value: any) {
  if (value === undefined || value === null || value === '') return '-';

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function extractUploadState(step: any) {
  return valueOf(
    step?.result?.upload_status?.status,
    step?.result?.upload_status?.current_status,
    step?.result?.upload_response?.status,
    step?.result?.status,
  );
}

function extractIngestionState(step: any) {
  return valueOf(
    step?.result?.ingestion_status?.current_status,
    step?.result?.ingestion_status?.status,
    step?.result?.ingestion_response?.current_status,
    step?.result?.ingestion_response?.status,
  );
}

function extractPipelineState(step: any) {
  return valueOf(
    step?.result?.pipeline_status?.status,
    step?.result?.pipeline_status?.current_status,
  );
}

const ManagedUploadStatusPanel: React.FC<Props> = ({ status, busy }) => {
  if (!status && !busy) return null;

  const steps = Array.isArray(status?.steps) ? status.steps : [];
  const mode = status?.mode || 'upload';
  const files = Array.isArray(status?.files) ? status.files : [];

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle1" fontWeight={700}>
            Managed Upload Status
          </Typography>

          <Chip size="small" variant="outlined" label={`Mode: ${mode}`} />

          {status?.ok !== undefined ? (
            <Chip
              size="small"
              color={status.ok ? 'success' : 'error'}
              variant="outlined"
              label={status.ok ? 'OK' : 'Failed'}
            />
          ) : null}
        </Stack>

        {busy ? <LinearProgress /> : null}

        {status?.pipeline_group ? (
          <Alert severity="info">
            Blue/green managed upload detected profile{' '}
            <b>{status.pipeline_group.name || status.pipeline_group.key}</b>. The standby pipeline is updated and
            validated first, then the profile temporarily cuts over, updates the original active pipeline, and restores
            the original active slot after both pipelines validate.
          </Alert>
        ) : null}

        {files.length ? (
          <Typography variant="body2" color="text.secondary">
            Files: <b>{files.join(', ')}</b>
          </Typography>
        ) : null}

        {steps.length ? (
          <Stack spacing={1.25}>
            {steps.map((step: any, idx: number) => {
              const uploadState = extractUploadState(step);
              const ingestionState = extractIngestionState(step);
              const pipelineState = extractPipelineState(step);

              return (
                <Paper key={`${step.step || 'step'}-${idx}`} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                      <Chip size="small" label={`Step ${idx + 1}`} />
                      <Typography variant="body2" fontWeight={700}>
                        {String(step.step || 'Step').replace(/_/g, ' ')}
                      </Typography>
                      {step.slot ? <Chip size="small" variant="outlined" label={`Slot: ${step.slot}`} /> : null}
                      {step.pipeline_id ? (
                        <Chip size="small" variant="outlined" label={`Pipeline: ${step.pipeline_id}`} />
                      ) : null}
                    </Stack>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {uploadState ? (
                        <Chip
                          size="small"
                          color={statusColor(uploadState)}
                          variant="outlined"
                          label={`Upload: ${uploadState}`}
                        />
                      ) : null}

                      {ingestionState ? (
                        <Chip
                          size="small"
                          color={statusColor(ingestionState)}
                          variant="outlined"
                          label={`Ingestion: ${ingestionState}`}
                        />
                      ) : null}

                      {pipelineState ? (
                        <Chip
                          size="small"
                          color={statusColor(pipelineState)}
                          variant="outlined"
                          label={`Pipeline: ${pipelineState}`}
                        />
                      ) : null}

                      {step.active_slot ? (
                        <Chip
                          size="small"
                          color="success"
                          variant="outlined"
                          label={`Active: ${step.active_slot}`}
                        />
                      ) : null}

                      {step.ingest_slot ? (
                        <Chip
                          size="small"
                          color="warning"
                          variant="outlined"
                          label={`Ingest: ${step.ingest_slot}`}
                        />
                      ) : null}
                    </Stack>

                    {step?.result?.execution_id || step?.result?.ingestion_response?.pipeline_executionid ? (
                      <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                        Execution ID:{' '}
                        {step.result.execution_id || step.result.ingestion_response.pipeline_executionid}
                      </Typography>
                    ) : null}

                    {step?.result?.task_id || step?.result?.upload_response?.task_id ? (
                      <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                        Upload Task ID: {step.result.task_id || step.result.upload_response.task_id}
                      </Typography>
                    ) : null}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        ) : status ? (
          <Box
            sx={{
              p: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'grey.50',
              maxHeight: 300,
              overflow: 'auto',
            }}
          >
            <Typography
              variant="caption"
              component="pre"
              sx={{
                m: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'monospace',
              }}
            >
              {stringifyValue(status)}
            </Typography>
          </Box>
        ) : null}

        {status?.steps?.length ? (
          <>
            <Divider />
            <Typography variant="caption" color="text.secondary">
              Final file list refreshes after managed upload completion. If a DRIFT operation is still processing,
              use Refresh to reload the latest pipeline state.
            </Typography>
          </>
        ) : null}
      </Stack>
    </Paper>
  );
};

export default ManagedUploadStatusPanel;